import { postJson } from "./http";
import type { ZohoOrganisationDTO } from "./zohoRecordsApi";

/**
 * Getting into a client's Zoho Books and proving we did: the bot seat, the
 * connection state machine the add-zournal screen drives, the read-back that
 * follows a connection, and the prepare-zoho org housekeeping.
 */

/**
 * The bot Zoho address reserved for this client, and how far the connection has
 * got. Deliberately narrow — this is what the add-zournal screen renders, so it
 * carries no invite URL and no internal error text.
 */
export interface ZohoSeatDTO {
  claimed_email: string | null;
  /** The client's own Zoho org, given before the address is shown. */
  expected_org_id: string | null;
  /**
   * Which Zoho datacentre the client's books live on ("in", "com"). Learnt from
   * the pasted Books URL or set by hand; null until then and read as ".in".
   * Decides which bot the address above belongs to.
   */
  dc: string | null;
  dc_label: string;
  status:
    | "seat_claimed"
    | "awaiting_invite"
    | "invite_found"
    | "accepting"
    | "accepted"
    | "verified"
    | "connected"
    | "failed";
  zoho_organization_id: string | null;
  organization_name: string | null;
  plan_name: string | null;
  user_role: string | null;
  invite_found_at: string | null;
  accepted_at: string | null;
  verified_at: string | null;
  claimed_at: string | null;
}

interface SeatEnvelope {
  success: boolean;
  message: string;
  data: { seat: ZohoSeatDTO };
}

/**
 * Reserve the bot address for this client. Idempotent, and safe to call on every
 * render of add-zournal: the same address always comes back, because the client
 * may already have typed it into their Zoho Books.
 */
export const claimZohoSeat = async (publicId: string): Promise<ZohoSeatDTO> =>
  (await postJson<SeatEnvelope>("/api/onboarding/zoho/seat/claim", { public_id: publicId })).data.seat;

/**
 * How far the connection has got. A phase, a sentence to show, and the org once
 * it is real — deliberately nothing else, so there is no internal detail to leak
 * onto a screen a client is looking at.
 */
export interface ZohoConnectionDTO {
  phase:
    | "seat_claimed"
    | "awaiting_invite"
    | "invite_found"
    | "accepting"
    | "accepted"
    | "connected"
    | "failed";
  message: string;
  claimed_email: string | null;
  expected_org_id: string | null;
  dc: string | null;
  dc_label: string;
  connected: boolean;
  waiting_for_invite: boolean;
  zoho_organization_id: string | null;
  organization_name: string | null;
  plan_name: string | null;
  user_role: string | null;
  accepted_at: string | null;
  verified_at: string | null;
  /**
   * Why a parked connection parked, in words an operator can act on — the
   * client added us as "Staff", no invitation ever arrived. `message` stays the
   * sentence you could read out to the client; this is the one you act on.
   */
  failure_reason: string | null;
  waiting_minutes: number;
}

/**
 * One row of the live sequence. The server folds the connection agent and the
 * read-back into this list, so the wording of a step and the state that decides
 * it stay in the same place rather than drifting apart across the wire.
 */
export interface ZohoStepDTO {
  key: string;
  label: string;
  status: "pending" | "running" | "done" | "failed";
  /** When it finished. Null while it is still going, or before it started. */
  at: string | null;
  /** What is happening right now, or what to check while waiting. */
  detail: string | null;
  error: string | null;
}

/** What we read out of the client's books once we were let in. */
export interface ZohoReadbackDTO {
  stages: {
    key: "organisation" | "settings";
    status: "pending" | "ok" | "failed";
    fetched_at: string | null;
    reading: string | null;
    error: string | null;
  }[];
  complete: boolean;
  organisation: ZohoOrganisationDTO | null;
  /**
   * The findings worth naming rather than counting. GSTINs and bank accounts
   * are listed because reading the actual numbers is how you notice you joined
   * the wrong books; TDS and TCS report how many rates have expired, which on
   * a real organisation is often all of them.
   */
  highlights: {
    gstins: string[];
    bank_accounts: string[];
    /** Section and rate, as an accountant reads them: "194C (2%)". */
    tds_tcs: string[];
    tds_expired: number;
    tcs_expired: number;
  };
  counts: {
    gstins: number;
    bank_accounts: number;
    tds_rates: number;
    tcs_rates: number;
    currencies: number;
    tax_rates: number;
    ledgers: number;
    /** Null until that count has been read — distinct from a genuine zero. */
    invoices: number | null;
    bills: number | null;
  };
}

export interface ZohoConnectStateDTO {
  connection: ZohoConnectionDTO;
  readback: ZohoReadbackDTO;
  steps: ZohoStepDTO[];
}

interface ConnectionEnvelope {
  success: boolean;
  message: string;
  data: ZohoConnectStateDTO;
}

const connect = async (
  action: string,
  publicId: string,
  extra: Record<string, unknown> = {}
): Promise<ZohoConnectStateDTO> =>
  (await postJson<ConnectionEnvelope>(`/api/onboarding/zoho/connect/${action}`, {
    public_id: publicId,
    ...extra,
  })).data;

/**
 * Name the organisation before anything is invited into it.
 *
 * This is what makes the connection verifiable: membership is checked against
 * this id, never inferred from whichever organisation happens to appear. Accepts
 * the number or the Books URL it sits in — the server extracts it either way.
 */
export const setZohoOrgId = async (publicId: string, orgId: string): Promise<ZohoConnectionDTO> =>
  (await connect("org-id", publicId, { org_id: orgId })).connection;

/**
 * Set the client's Zoho datacentre by hand. Only while the invite is still
 * unaccepted: the seat is released and re-claimed on a bot of that DC, so the
 * address on screen changes — the caller must re-read the seat.
 */
export const setZohoDc = async (publicId: string, dc: string): Promise<ZohoConnectionDTO> =>
  (await connect("dc", publicId, { dc })).connection;

/** The client has added our address; the agent takes over from here. */
export const startZohoConnect = (publicId: string) => connect("start", publicId);

/**
 * One step per call — this is what the screen polls. Guarded server-side by the
 * seat's own status and by each dataset's own record, so overlapping polls
 * cannot start two browser runs or read the same dataset twice.
 */
export const advanceZohoConnect = (publicId: string) => connect("advance", publicId);

/** Look without touching: reads nothing from Zoho and starts no clock. */
export const zohoConnectStatus = (publicId: string) => connect("status", publicId);

/**
 * Re-enter the state machine from the last durable point. Every waiting state
 * now parks on a deadline rather than spinning, so there has to be a way back in
 * once whatever it was waiting for has been sorted out.
 */
export const retryZohoConnect = (publicId: string) => connect("retry", publicId);

/**
 * Try the reads that failed, and only those. Re-pulling a hundred and twenty
 * ledgers that came back fine is how a CA's confirmations get churned for
 * nothing.
 */
export const retryZohoReadback = (publicId: string) => connect("readback-retry", publicId);

/** Where the prepare-zoho housekeeping stands, one row per org setting. */
export interface PrepareZohoStateDTO {
  steps: ZohoStepDTO[];
}

/**
 * Look at every org setting the prepare-zoho step owns and fix what needs
 * fixing. Safe to call on every visit: the server re-checks each setting
 * before touching it, so a revisit that finds everything in order writes
 * nothing. A setting Zoho refused comes back as a failed row, not an error.
 */
export const runPrepareZoho = async (publicId: string): Promise<PrepareZohoStateDTO> =>
  (
    await postJson<{ success: boolean; message: string; data: PrepareZohoStateDTO }>(
      "/api/onboarding/zoho/prepare",
      { public_id: publicId }
    )
  ).data;
